import React, { useEffect, useState } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Form,
  Button,
  Message,
  Loader,
  Icon,
  Grid,
  TextArea,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, getAuthHeaders } from '../../api';

export default function DocumentEditor() {
  const { id } = useParams();
  const history = useHistory();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form fields
  const [title, setTitle] = useState('');
  const [version, setVersion] = useState('');
  const [status, setStatus] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [domainCode, setDomainCode] = useState('');
  const [classification, setClassification] = useState('');
  const [owner, setOwner] = useState('');
  const [author, setAuthor] = useState('');
  const [reviewer, setReviewer] = useState('');
  const [approver, setApprover] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [nextReviewDate, setNextReviewDate] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [bodyMd, setBodyMd] = useState('');
  const [kbSlug, setKbSlug] = useState(null);

  useEffect(() => {
    fetch(`${getApiPath()}/@qms/register/${encodeURIComponent(id)}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => {
        if (res.status === 401) {
          window.location.href = `/login?return_url=${encodeURIComponent(window.location.pathname)}`;
          throw new Error('Session expired');
        }
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return res.json();
      })
      .then((json) => {
        setDoc(json);
        setTitle(json.title || '');
        setVersion(json.version || '');
        setStatus(json.status || '');
        setDocumentType(json.document_type || '');
        setDomainCode(json.domain_code || '');
        setClassification(json.classification || '');
        setOwner(json.owner || '');
        setAuthor(json.author || '');
        setReviewer(json.reviewer || '');
        setApprover(json.approver || '');
        setEffectiveDate(json.effective_date || '');
        setNextReviewDate(json.next_review_date || '');
        setLocation(json.location || '');
        setNotes(json.notes || '');
        setBodyMd(json.body_md || '');
        setKbSlug(json.kb_slug || null);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(
        `${getApiPath()}/@qms/register/${encodeURIComponent(id)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            title,
            version,
            status,
            document_type: documentType,
            domain_code: domainCode,
            classification,
            owner: owner || null,
            author: author || null,
            reviewer: reviewer || null,
            approver: approver || null,
            effective_date: effectiveDate || null,
            next_review_date: nextReviewDate || null,
            location: location || null,
            notes: notes || null,
            body_md: bodyMd || null,
          }),
        },
      );

      if (res.status === 401) {
        window.location.href = `/login?return_url=${encodeURIComponent(window.location.pathname)}`;
        return;
      }
      if (res.status === 403) {
        setError('Permission denied. You need the Manage Documents permission.');
        setSaving(false);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `${res.status} ${res.statusText}`);
      }

      setSuccess('Document saved successfully.');
      setSaving(false);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container style={{ paddingTop: '3em' }}>
        <Loader active inline="centered" content="Loading document..." />
      </Container>
    );
  }

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '3em' }}>
      <Helmet title={`Edit ${id}`} />
      <Link to={`/qms/register/${encodeURIComponent(id)}`}>
        &larr; Back to {id}
      </Link>

      <Header as="h1" style={{ marginTop: '0.5em' }}>
        <Icon name="edit" /> Edit {id}
        <Header.Subheader>{doc?.title}</Header.Subheader>
      </Header>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}
      {success && (
        <Message positive>
          <Message.Header>Saved</Message.Header>
          <p>{success}</p>
        </Message>
      )}

      <Form>
        {/* Metadata */}
        <Segment>
          <Header as="h3">Metadata</Header>
          <Grid columns={2} stackable>
            <Grid.Column>
              <Form.Input
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <Form.Input
                label="Version"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
              />
              <Form.Select
                label="Status"
                value={status}
                onChange={(e, { value }) => setStatus(value)}
                options={[
                  { key: 'draft', value: 'draft', text: 'Draft' },
                  { key: 'in_review', value: 'in_review', text: 'In Review' },
                  { key: 'effective', value: 'effective', text: 'Effective' },
                  {
                    key: 'superseded',
                    value: 'superseded',
                    text: 'Superseded',
                  },
                  { key: 'obsolete', value: 'obsolete', text: 'Obsolete' },
                ]}
              />
              <Form.Select
                label="Document Type"
                value={documentType}
                onChange={(e, { value }) => setDocumentType(value)}
                options={[
                  { key: 'POL', value: 'POL', text: 'Policy' },
                  { key: 'SOP', value: 'SOP', text: 'SOP' },
                  { key: 'WI', value: 'WI', text: 'Work Instruction' },
                  { key: 'FRM', value: 'FRM', text: 'Form / Template' },
                  { key: 'TECH', value: 'TECH', text: 'Technical Document' },
                  { key: 'REC', value: 'REC', text: 'Record' },
                  { key: 'EXT', value: 'EXT', text: 'External Document' },
                ]}
              />
              <Form.Select
                label="Domain"
                value={domainCode}
                onChange={(e, { value }) => setDomainCode(value)}
                options={[
                  { key: 'DC', value: 'DC', text: 'Document Control' },
                  { key: 'QMS', value: 'QMS', text: 'Quality Management' },
                  { key: 'RM', value: 'RM', text: 'Risk Management' },
                  { key: 'SW', value: 'SW', text: 'Software' },
                  { key: 'CM', value: 'CM', text: 'Change Management' },
                  { key: 'CS', value: 'CS', text: 'Cybersecurity' },
                  { key: 'UE', value: 'UE', text: 'Usability Engineering' },
                  { key: 'TR', value: 'TR', text: 'Translation' },
                  { key: 'REG', value: 'REG', text: 'Regulatory' },
                  { key: 'SUP', value: 'SUP', text: 'Supplier / Procurement' },
                  { key: 'HR', value: 'HR', text: 'Human Resources' },
                ]}
              />
            </Grid.Column>
            <Grid.Column>
              <Form.Select
                label="Classification"
                value={classification}
                onChange={(e, { value }) => setClassification(value)}
                options={[
                  { key: 'public', value: 'public', text: 'Public' },
                  { key: 'internal', value: 'internal', text: 'Internal' },
                  {
                    key: 'confidential',
                    value: 'confidential',
                    text: 'Confidential',
                  },
                ]}
              />
              <Form.Input
                label="Owner"
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
              />
              <Form.Input
                label="Author"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
              />
              <Form.Input
                label="Reviewer"
                value={reviewer}
                onChange={(e) => setReviewer(e.target.value)}
              />
              <Form.Input
                label="Approver"
                value={approver}
                onChange={(e) => setApprover(e.target.value)}
              />
              <Form.Input
                label="Effective Date"
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
              />
              <Form.Input
                label="Next Review Date"
                type="date"
                value={nextReviewDate}
                onChange={(e) => setNextReviewDate(e.target.value)}
              />
            </Grid.Column>
          </Grid>
          <Form.Input
            label="File Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g. docs/qms/POL-DC-001_Document_Control_Policy.md"
          />
          <Form.Input
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Segment>

        {/* Body editor — hidden for KB-linked documents */}
        {kbSlug ? (
          <Segment secondary>
            <Icon name="linkify" />
            Document content is managed in the{' '}
            <Link to={`/docs/${kbSlug}`}>Knowledge Base</Link>.{' '}
            <Link to={`/docs/${kbSlug}/edit`}>Edit content &rarr;</Link>
          </Segment>
        ) : (
          <Segment>
            <Header as="h3">Document Body (Markdown)</Header>
            <Form.Field
              control={TextArea}
              value={bodyMd}
              onChange={(e) => setBodyMd(e.target.value)}
              rows={20}
              style={{ fontFamily: 'monospace', fontSize: '0.9em' }}
              placeholder="Write document content in Markdown..."
            />
          </Segment>
        )}

        <Button primary loading={saving} onClick={handleSave}>
          <Icon name="save" /> Save Changes
        </Button>
        <Button
          as={Link}
          to={`/qms/register/${encodeURIComponent(id)}`}
        >
          Cancel
        </Button>
      </Form>
    </Container>
  );
}
